import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Collapse, Tooltip, Typography } from "@mui/material";
import Icon from "@mui/material/Icon";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { Layer } from "../../types/layer";
import VegetationControl from "./VegetationControl";
import FuelLoadControl from "./FuelLoadControl";
import GrassCuringControl from "./GrassCuringControl";
import FuelLoad3DControl from "./FuelLoad3DControl";

interface FuelsGroupProps {
  vegetationLayer: Layer | null;
  fuelLoadLayer: Layer | null;
  grassCuringLayer: Layer | null;
  fuelLoad3DLayer: Layer | null;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

type FuelKey = "vegetation" | "fuelLoad" | "curing" | "fuel3d";

const enabledOf = (key: FuelKey, layer: Layer): boolean =>
  key === "vegetation"
    ? !!layer.rawSource
    : (layer.toolbarButton?.props?.enabled ?? (key === "curing" ? false : true));

/**
 * The fuel layers under one parent: vegetation, fuel load, grass curing and
 * the 3D view of them.
 *
 * Hover the parent and it bursts into the four layers, each the same control
 * it always was -- its own eye, its own settings panel. Nothing is buried a
 * level down. The parent's eye is the group's: everything off if anything is
 * on, otherwise everything on.
 *
 * The burst stays open while any child's settings panel is in use. That
 * panel is a portal drawn outside this box, so the pointer leaving the box
 * is not enough to say the user is done here.
 */
const FuelsGroup: React.FC<FuelsGroupProps> = ({
  vegetationLayer,
  fuelLoadLayer,
  grassCuringLayer,
  fuelLoad3DLayer,
  expanded = false,
  onExpandedChange,
}) => {
  const [hovered, setHovered] = useState(false);
  const [openPanels, setOpenPanels] = useState<ReadonlySet<FuelKey>>(() => new Set());
  const leaveTimer = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    if (leaveTimer.current) {
      clearTimeout(leaveTimer.current);
      leaveTimer.current = null;
    }
    setHovered(true);
    onExpandedChange?.(true);
  };
  const handleMouseLeave = () => {
    leaveTimer.current = setTimeout(() => setHovered(false), 250);
  };
  useEffect(() => () => {
    if (leaveTimer.current) clearTimeout(leaveTimer.current);
  }, []);

  // One stable callback per child. The child calls it from an effect, so a
  // fresh function each render would re-fire the effect each render; and the
  // updater returns the same set when nothing changed, so a repeated "open"
  // is not a state change.
  const panelChanged = useCallback((key: FuelKey, open: boolean) => {
    setOpenPanels((prev) => {
      if (prev.has(key) === open) return prev;
      const next = new Set(prev);
      if (open) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);
  const onVegetationPanel = useCallback((o: boolean) => panelChanged("vegetation", o), [panelChanged]);
  const onFuelLoadPanel = useCallback((o: boolean) => panelChanged("fuelLoad", o), [panelChanged]);
  const onCuringPanel = useCallback((o: boolean) => panelChanged("curing", o), [panelChanged]);
  const onFuel3DPanel = useCallback((o: boolean) => panelChanged("fuel3d", o), [panelChanged]);

  const members: [FuelKey, Layer | null][] = [
    ["vegetation", vegetationLayer],
    ["fuelLoad", fuelLoadLayer],
    ["curing", grassCuringLayer],
    ["fuel3d", fuelLoad3DLayer],
  ];
  const present = members.filter((m): m is [FuelKey, Layer] => m[1] !== null);
  if (!present.length) return null;

  const usable = present.filter(([key, layer]) => enabledOf(key, layer));
  const anyVisible = usable.some(([, layer]) => layer.active);
  const handleToggleAll = () => {
    for (const [, layer] of usable) {
      if (Boolean(layer.active) === anyVisible) layer.toolbarButton?.props?.onClick?.();
    }
  };

  const burst = hovered || openPanels.size > 0;
  const isExpanded = expanded || hovered;

  return (
    <Box onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {/* Parent pill, matching the other rows' button. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          minHeight: 60,
          width: "100%",
        }}
      >
        <Tooltip title={usable.length ? (anyVisible ? "Hide all fuel layers" : "Show all fuel layers") : ""} placement="left">
          <Box
            onClick={usable.length ? handleToggleAll : undefined}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: isExpanded ? 1 : 0,
              width: isExpanded ? 160 : 48,
              minWidth: 48,
              height: 48,
              paddingX: isExpanded ? 2 : 0,
              paddingRight: isExpanded ? 2.5 : 0,
              borderRadius: isExpanded ? "24px" : "50%",
              backgroundColor: anyVisible ? "#00d4ad" : "rgba(0, 212, 173, 0.3)",
              color: "#fff",
              cursor: usable.length ? "pointer" : "default",
              transition: "all 0.3s ease-in-out",
              border: "2px solid rgba(148, 163, 184, 0.2)",
              "&:hover": {
                backgroundColor: "#faad17",
                transform: "scale(1.05)",
              },
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            <Icon>forest</Icon>
            {isExpanded && (
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: "#fff",
                  flex: 1,
                  animation: "fadeIn 0.3s ease-in",
                  "@keyframes fadeIn": { from: { opacity: 0 }, to: { opacity: 1 } },
                }}
              >
                Fuels
              </Typography>
            )}
            {isExpanded && (
              <ExpandMoreIcon
                sx={{
                  fontSize: "1.1rem",
                  transition: "transform 0.2s ease-in-out",
                  transform: burst ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            )}
          </Box>
        </Tooltip>
      </Box>

      {/* The burst: the four layers, indented under the parent. */}
      <Collapse in={burst} timeout={200} unmountOnExit>
        <Box
          sx={{
            pr: 1.5,
            mr: 2.5,
            borderRight: "2px solid rgba(0, 212, 173, 0.35)",
          }}
        >
          <VegetationControl layer={vegetationLayer} expanded={expanded} onExpandedChange={onExpandedChange} onPanelOpenChange={onVegetationPanel} />
          <FuelLoadControl layer={fuelLoadLayer} expanded={expanded} onExpandedChange={onExpandedChange} onPanelOpenChange={onFuelLoadPanel} />
          <GrassCuringControl layer={grassCuringLayer} expanded={expanded} onExpandedChange={onExpandedChange} onPanelOpenChange={onCuringPanel} />
          <FuelLoad3DControl layer={fuelLoad3DLayer} expanded={expanded} onExpandedChange={onExpandedChange} onPanelOpenChange={onFuel3DPanel} />
        </Box>
      </Collapse>
    </Box>
  );
};

export default FuelsGroup;
